import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PenLine } from 'lucide-react';
import { SocialMediaPostPreview } from '../posts/SocialMediaPostPreview';
import type { SocialPlatform } from '../icons/SocialIcon';
import { useBrands } from '../../hooks/useBrands';
import type { PlatformContent } from '../../services/contentService';
import { logger } from '../../utils/logger';

interface ContentResultCardProps {
  platform: string;
  content: string | PlatformContent;
  hashtags?: string[];
  onCreatePost?: (platform: string, content: string, hashtags?: string[]) => void;
  brandId?: string;
  format?: 'regular' | 'reel';
  imageUrl?: string | null;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
}

export const ContentResultCard: React.FC<ContentResultCardProps> = ({
  platform,
  content,
  hashtags,
  onCreatePost,
  brandId,
  format = 'regular',
  imageUrl,
  isSelected = false,
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const { data: brandsData } = useBrands();
  
  // Ensure content is a string
  let contentString = '';
  
  if (typeof content === 'string') {
    // Replace escaped newlines with real ones
    contentString = content.replace(/\\n/g, '\n');
    // Log to see if newlines exist in the original string
    if (process.env.NODE_ENV === 'development') {
      logger.debug('ContentResultCard', `${platform} content newline count`, (contentString.match(/\n/g) || []).length);
    }
  } else if (content && typeof content === 'object') {
    // Check if it has a text property (PlatformContent)
    if ('text' in content && content.text) {
      contentString = typeof content.text === 'string' 
        ? content.text.replace(/\\n/g, '\n')
        : JSON.stringify(content.text);
      if (process.env.NODE_ENV === 'development' && typeof content.text === 'string') {
        const processedText = content.text.replace(/\\n/g, '\n');
        logger.debug('ContentResultCard', `${platform} content.text newline count`, (processedText.match(/\n/g) || []).length);
      }
    } else {
      // Otherwise stringify the whole object
      contentString = JSON.stringify(content);
    }
  } else {
    contentString = String(content);
  }
  
  // Ensure hashtags are strings
  const hashtagsArray = hashtags?.map(tag => 
    typeof tag === 'string' ? tag : JSON.stringify(tag)
  ) || [];


  
  // Get the brand data if brandId is provided
  const brands = brandsData ? (brandsData as any).brands || [] : [];
  const brand = brandId ? brands.find((b: any) => b.id === brandId) : undefined;



  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return '📸';
      case 'linkedin':
        return '💼';
      case 'facebook':
        return '👍';
      case 'twitter':
        return '🐦';
      case 'tiktok':
        return '🎵';
      case 'youtube':
        return '🎬';
      default:
        return '📱';
    }
  };

  // Convert platform string to SocialPlatform type
  const getSocialPlatform = (platform: string): SocialPlatform => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return 'instagram';
      case 'linkedin':
        return 'linkedin';
      case 'facebook':
        return 'facebook';
      case 'twitter':
        return 'twitter';
      case 'tiktok':
        return 'tiktok';
      case 'youtube':
        return 'youtube';
      case 'google-business':
        return 'google-business';
      default:
        return 'linkedin';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-black">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center">
          <span className="text-xl mr-2">{getPlatformIcon(platform)}</span>
          <h3 className="font-medium text-gray-800 capitalize">{platform}</h3>
        </div>
        <div className="flex items-center space-x-2">
          {onCreatePost && (
            <button
              onClick={() => onCreatePost(platform, contentString, hashtagsArray)}
              className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
              title={t('contentCreation.results.createPost')}
            >
              <PenLine className="h-5 w-5 text-gray-600" />
            </button>
          )}
          {onSelectionChange && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelectionChange(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          )}
        </div>
      </div>
      
      {/* Post Preview - Only show the preview, no raw content or hashtags */}
      <div className="p-4">
        <SocialMediaPostPreview 
          platform={getSocialPlatform(platform)}
          content={content}
          brand={brand}
          format={format}
          media={imageUrl ? { 
            url: imageUrl, 
            type: 'image',
            uploadedAt: new Date().toISOString()
          } : undefined}
        />
      </div>
    </div>
  );
}; 